import React from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import type { Site } from '../../services/sitesApi';
import type { WorkspacePrimaryCta } from '../../domain/safetyCases/workspacePrimaryCta';
import { WorkspaceLifecycleRail } from './WorkspaceLifecycleRail';
import { WorkspaceScopeSelector } from './WorkspaceScopeSelector';
import { WorkspaceStatusLegend } from './WorkspaceStatusLegend';

type TimeWindowOption = {
  key: string;
  label: string;
  disabled?: boolean;
};

/**
 * Compact workspace header: title, one-line subtitle, site | time | primary CTA.
 * Slice 2 chrome extraction keeps the portfolio route under complexity caps.
 */
export function WorkspaceHeaderChrome({
  title,
  subtitle,
  loading,
  orgId,
  siteId,
  scopeMode,
  sites,
  sitesLoading,
  sitesError,
  onSiteChange,
  windowLabel,
  timeWindowMenuAnchorEl,
  onOpenTimeWindowMenu,
  onCloseTimeWindowMenu,
  timeWindowOptions,
  onTimeWindowSelect,
  primaryCta,
  onPrimaryCta,
  showPortfolioExport,
  onPortfolioExport,
}: {
  title: string;
  subtitle: string;
  loading?: boolean;
  orgId: string;
  siteId: string;
  scopeMode: 'site' | 'org';
  sites: Site[];
  sitesLoading?: boolean;
  sitesError?: string | null;
  onSiteChange: (siteId: string) => void;
  windowLabel: string;
  timeWindowMenuAnchorEl: HTMLElement | null;
  onOpenTimeWindowMenu: (event: React.MouseEvent<HTMLElement>) => void;
  onCloseTimeWindowMenu: () => void;
  timeWindowOptions: TimeWindowOption[];
  onTimeWindowSelect: (key: string) => void;
  primaryCta: WorkspacePrimaryCta;
  onPrimaryCta: () => void;
  showPortfolioExport?: boolean;
  onPortfolioExport?: () => void;
}) {
  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.25, letterSpacing: '-0.01em' }}>
        {loading ? <Skeleton variant="text" width={320} /> : title}
      </Typography>
      <Typography color="text.secondary" sx={{ fontSize: '0.8125rem', mb: 0.25, lineHeight: 1.35 }}>
        {subtitle}
      </Typography>
      <WorkspaceLifecycleRail />
      <WorkspaceStatusLegend />
      <Stack
        direction="row"
        spacing={1.25}
        flexWrap="wrap"
        useFlexGap
        alignItems="flex-start"
        data-testid="workspace-header-controls"
        sx={{ mb: 0.5 }}
      >
        <Box sx={{ flex: '1 1 280px', minWidth: 240, maxWidth: 520 }}>
          <WorkspaceScopeSelector
            orgId={orgId}
            siteId={siteId}
            mode={scopeMode}
            sites={sites}
            loading={sitesLoading}
            error={sitesError}
            onSiteChange={onSiteChange}
          />
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<CalendarTodayOutlinedIcon sx={{ fontSize: 16 }} />}
          endIcon={<KeyboardArrowDownRoundedIcon />}
          onClick={onOpenTimeWindowMenu}
          aria-haspopup="menu"
          aria-controls={timeWindowMenuAnchorEl ? 'safety-reports-time-window-menu' : undefined}
          data-testid="workspace-time-window-button"
          sx={{
            borderRadius: '999px',
            px: 1.25,
            py: 0.75,
            borderColor: 'rgba(37, 99, 235, 0.32)',
            color: 'text.primary',
            textTransform: 'none',
            fontWeight: 600,
            mt: 0.25,
          }}
        >
          {`Time window: ${windowLabel}`}
        </Button>
        {primaryCta.kind !== 'none' ? (
          <Button
            size="small"
            variant="contained"
            startIcon={
              primaryCta.kind === 'manage_recurring_scans'
                ? <CalendarTodayOutlinedIcon sx={{ fontSize: 16 }} />
                : undefined
            }
            onClick={onPrimaryCta}
            data-testid={primaryCta.testId}
            sx={{
              borderRadius: '999px',
              px: 1.25,
              py: 0.75,
              textTransform: 'none',
              fontWeight: 700,
              mt: 0.25,
            }}
          >
            {primaryCta.label}
          </Button>
        ) : null}
        {showPortfolioExport ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={onPortfolioExport}
            data-testid="workspace-portfolio-export-csv"
            sx={{
              borderRadius: '999px',
              px: 1.25,
              py: 0.75,
              textTransform: 'none',
              fontWeight: 600,
              mt: 0.25,
            }}
          >
            Export portfolio CSV
          </Button>
        ) : null}
      </Stack>
      <Menu
        id="safety-reports-time-window-menu"
        anchorEl={timeWindowMenuAnchorEl}
        open={Boolean(timeWindowMenuAnchorEl)}
        onClose={onCloseTimeWindowMenu}
      >
        {timeWindowOptions.map((option) => (
          <MenuItem
            key={option.key}
            disabled={option.disabled}
            onClick={() => onTimeWindowSelect(option.key)}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
